import { inject, Injectable } from '@angular/core';
import { firstValueFrom, pipe } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import {
  BudgetType,
  ContactType,
  Currency,
  EntityType,
  GqlService,
  listOrganizationsWithTotalBudgetAmountQuery,
  OrganizationType,
  VendorType,
} from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';
import { MainQuery } from '@shared/store/main/main.query';

import { OrganizationModel, OrganizationStore } from './organization.store';

@Injectable({ providedIn: 'root' })
export class OrganizationService {
  private gqlService = inject(GqlService);
  private mainQuery = inject(MainQuery);
  private overlayService = inject(OverlayService);
  private vendorsStore = inject(OrganizationStore);

  // eslint-disable-next-line max-lines-per-function
  async add(
    name: string,
    {
      costs_included_in_parent_wo,
      email,
      family_name,
      given_name,
      parent_organization_id,
      phone_number,
      receives_vendor_estimate,
      title,
      vendor_type,
    }: {
      costs_included_in_parent_wo: boolean;
      email: string;
      family_name: string;
      given_name: string;
      parent_organization_id: string;
      phone_number: string;
      receives_vendor_estimate: boolean;
      title: string;
      vendor_type: VendorType;
    },
    currency: Currency | null = null,
    organization_type: OrganizationType = OrganizationType.ORGANIZATION_VENDOR
  ) {
    let operationSuccess = false;
    let operationErrors;
    let organizationData = null;

    this.vendorsStore.setLoading(true);
    try {
      const { data, errors, success } = await firstValueFrom(
        this.gqlService.createOrganization$({
          costs_included_in_parent_wo,
          currency,
          name,
          organization_type,
          parent_organization_id,
          receives_vendor_estimate,
          vendor_type,
        })
      );

      if (success && data) {
        organizationData = data;
        this.vendorsStore.add({
          __typename: 'Organization',
          baseline_budget_version: null,
          budget_version: {},
          costs_included_in_parent_wo,
          currency: organizationData.currency,
          id: organizationData.id,
          name: organizationData.name,
          organization_type: organizationData.organization_type,
          parent_organization: organizationData.parent_organization || undefined,
          receives_vendor_estimate,
          vendor_type: vendor_type as VendorType,
        });

        if (given_name || family_name || email || title || phone_number) {
          const { errors: vError, success: vSuccess } = await firstValueFrom(
            this.gqlService.createContact$({
              contact_type: ContactType.CONTACT_VENDOR,
              email: email === '' ? null : email,
              entity_id: organizationData.id,
              entity_type: EntityType.ORGANIZATION,
              family_name,
              given_name,
              phone_number,
              title,
            })
          );

          operationSuccess = vSuccess;
          operationErrors = vSuccess ? [] : vError;
        } else {
          operationSuccess = true;
        }
      } else {
        operationErrors = errors;
      }
    } finally {
      this.vendorsStore.setLoading(false);
    }

    return { data: organizationData, errors: operationErrors, success: operationSuccess };
  }

  get(includeInactive = false) {
    return this.mainQuery.select('trialKey').pipe(
      switchMap(() => {
        this.vendorsStore.setLoading(true);

        return this.gqlService.listOrganizations$(includeInactive).pipe(
          tap(({ data }) => {
            this.vendorsStore.set(this.organizationMapper(data || []));
            this.vendorsStore.setLoading(false);
          })
        );
      })
    );
  }

  getContacts(entity_id: string) {
    return this.gqlService.listContacts$(entity_id, ContactType.CONTACT_VENDOR).pipe(
      map((x) => {
        return { ...x, data: x.data ? x.data[0] : null };
      })
    );
  }

  // TODO need make 1 call in main layout and share Vendor Contract list in other components
  getListWithTotalBudgetAmount(budgetType?: BudgetType) {
    return this.mainQuery
      .select('trialKey')
      .pipe(this.getListWithTotalBudgetAmountPipeline(budgetType));
  }

  getListWithTotalBudgetAmountPipeline(budgetType?: BudgetType, includeInactive = false) {
    return pipe(
      switchMap(() => {
        this.vendorsStore.setLoading(true);

        return this.gqlService
          .listOrganizationsWithTotalBudgetAmount$(includeInactive, budgetType)
          .pipe(
            tap(({ data }) => {
              this.vendorsStore.set(this.organizationMapper(data || []));
              this.vendorsStore.setLoading(false);
            })
          );
      })
    );
  }

  listIdOrganizations$(includeInactive = false) {
    this.vendorsStore.setLoading(true);

    return this.gqlService.listIdOrganizations$(includeInactive).pipe(
      tap(({ data }) => {
        this.vendorsStore.set((data || []).map((i) => ({ ...i, budget_version: {} })));
        this.vendorsStore.setLoading(false);
      })
    );
  }

  listOrganizationNames(includeInactive = false) {
    return this.gqlService.listOrganizationNames$(includeInactive).pipe(
      map((x) => {
        return { ...x, data: x.data ? x.data : null };
      })
    );
  }

  listVendorAmountTypes() {
    return this.gqlService.listVendorAmountTypes$().pipe(
      map((x) => {
        return { ...x, data: x.data ? x.data : null };
      })
    );
  }

  organizationMapper(arr: listOrganizationsWithTotalBudgetAmountQuery[]) {
    return arr.map((org) => {
      const versions: Record<
        string,
        {
          bucket_key: string;
          budget_name: string;
          budget_version_id: string;
          manual_forecast: boolean;
          total_budget_amount: number;
        }
      > = {};

      org.current_budget_versions.forEach((bv) => {
        versions[`${bv.budget_type}-${bv.entity_id}-${bv.entity_type}`] = {
          bucket_key: bv.bucket_key || '',
          budget_name: bv.budget_name || '',
          budget_version_id: bv.budget_version_id,
          manual_forecast: bv.manual_forecast,
          total_budget_amount: bv.total_budget_amount || 0,
        };
      });

      return { ...org, budget_version: versions };
    });
  }

  async remove(vendor: OrganizationModel) {
    const { errors, success } = await firstValueFrom(
      this.gqlService.removeOrganization$(vendor.id)
    );

    if (success) {
      this.overlayService.success(`${vendor.name} successfully removed!`);
      this.vendorsStore.remove(vendor.id);
    } else {
      this.overlayService.error(errors);
    }

    return { errors, success };
  }

  // eslint-disable-next-line max-lines-per-function
  async update(
    id: string,
    name: string,
    {
      costs_included_in_parent_wo,
      email,
      family_name,
      given_name,
      parent_organization_id,
      phone_number,
      receives_vendor_estimate,
      title,
      vendor_type,
    }: {
      costs_included_in_parent_wo: boolean;
      email: string;
      family_name: string;
      given_name: string;
      parent_organization_id: string;
      phone_number: string;
      receives_vendor_estimate: boolean;
      title: string;
      vendor_type: VendorType;
    },
    currency: Currency | null = null
  ) {
    let operationSuccess = false;
    let operationErrors;
    let organizationData = null;

    this.vendorsStore.setLoading(true);
    try {
      const { data, errors, success } = await firstValueFrom(
        this.gqlService.updateOrganization$({
          costs_included_in_parent_wo,
          currency,
          id,
          name,
          parent_organization_id,
          receives_vendor_estimate,
          vendor_type,
        })
      );

      if (success && data) {
        organizationData = data;
        this.vendorsStore.update(id, {
          ...data,
          __typename: 'Organization',
          costs_included_in_parent_wo,
          parent_organization: organizationData.parent_organization,
        });

        if (given_name || family_name || email || title || phone_number) {
          const getContactResult = await firstValueFrom(this.getContacts(id));

          // eslint-disable-next-line max-depth
          if (getContactResult.success) {
            let vSuccess;

            // eslint-disable-next-line max-depth
            if (getContactResult.data?.id) {
              const updateContactResult = await firstValueFrom(
                this.gqlService.updateContact$({
                  email: email === '' ? null : email,
                  family_name,
                  given_name,
                  id: getContactResult.data.id,
                  phone_number,
                  title,
                })
              );

              vSuccess = updateContactResult.success;
            } else {
              const createContactResult = await firstValueFrom(
                this.gqlService.createContact$({
                  contact_type: ContactType.CONTACT_VENDOR,
                  email: email === '' ? null : email,
                  entity_id: organizationData.id,
                  entity_type: EntityType.ORGANIZATION,
                  family_name,
                  given_name,
                  phone_number,
                  title,
                })
              );

              vSuccess = createContactResult.success;
            }

            operationSuccess = vSuccess;
          } else {
            operationErrors = getContactResult.errors;
          }
        } else {
          operationSuccess = true;
        }
      } else {
        operationErrors = errors;
      }
    } finally {
      this.vendorsStore.setLoading(false);
    }

    return { data: organizationData, errors: operationErrors, success: operationSuccess };
  }
}
